/**
 * Page fiche personnage complete.
 */

import type { BDDIndexes } from '@data/indexes';
import { loadIndexes } from '@data/indexes';
import { type CompagnonBase, loadCompagnons } from '@data/loaders';
import { CARACS, CARAC_ROLES } from '@domain/caracs';
import { type DerivedInput, calcDerived } from '@domain/derived';
import { FAMILLES, type FamilleId } from '@domain/famille';
import { duplicatePersonnage, exportPersonnageJSON, getPersonnage } from '@persistence/personnages';
import { html, nothing } from 'lit';
import { buildHref, navigate } from '../router';

const equipErrors: string[] = [];

function findEquipStats(itemName: string, idx: BDDIndexes): string | null {
  for (const a of idx.bdd.equipement.accessoires ?? []) {
    if (a.nom === itemName) {
      return a.description;
    }
  }
  const equip = idx.bdd.equipement;
  for (const a of equip.armures ?? []) {
    if (a.nom === itemName) {
      return `DEF +${a.bonus_def}, AGI max ${a.agi_max ?? 'aucun'}`;
    }
  }
  for (const b of equip.boucliers ?? []) {
    if (b.nom === itemName) {
      return `DEF +${b.bonus_def}`;
    }
  }
  for (const w of equip.armes_contact ?? []) {
    if (w.nom === itemName) {
      const parts = [`DM ${w.dm}`, w.type_dm];
      if (w.seuil_critique_min && w.seuil_critique_min < 20)
        parts.push(`crit ${w.seuil_critique_min}-20`);
      return parts.join(', ');
    }
  }
  for (const w of equip.armes_distance ?? []) {
    if (w.nom === itemName) {
      const parts = [`DM ${w.dm}`, `portee ${w.portee_normale} m`];
      if (w.seuil_critique_min && w.seuil_critique_min < 20)
        parts.push(`crit ${w.seuil_critique_min}-20`);
      return parts.join(', ');
    }
  }
  equipErrors.push(itemName);
  return null;
}

function renderRangDots(current: number, max: number) {
  return html`${Array.from(
    { length: max },
    (_, i) => html`<span class="rang-dot ${i < current ? 'filled' : ''}"></span>`,
  )}`;
}

function formatSign(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`;
}

function pvColorClass(current: number, max: number): string {
  if (max <= 0) return 'pv-full';
  const ratio = current / max;
  if (ratio >= 1) return 'pv-full';
  if (ratio >= 0.75) return 'pv-high';
  if (ratio >= 0.25) return 'pv-mid';
  return 'pv-low';
}

interface CapBonuses {
  pv: number;
  def: number;
  init: number;
  attContact: number;
  attDistance: number;
  attMagique: number;
  pm: number;
}

const WARRIOR_DEITIES = ['axender', 'forthur', 'guardal', 'irrion', 'vorona'];

function resolveCapBonuses(
  p: {
    voiesProfil: string[];
    voiePeupleId: string | null;
    rangs: Record<string, number>;
    choixVoies?: Record<string, string>;
    diviniteId?: string | null;
  },
  idx: BDDIndexes,
): CapBonuses {
  const bonuses: CapBonuses = {
    pv: 0,
    def: 0,
    init: 0,
    attContact: 0,
    attDistance: 0,
    attMagique: 0,
    pm: 0,
  };
  const allVoieIds = [...p.voiesProfil, ...(p.voiePeupleId ? [p.voiePeupleId] : [])];

  for (const vid of allVoieIds) {
    const rang = p.rangs[vid] ?? 0;
    const voie = idx.voieClasseById.get(vid) ?? idx.voiePeupleById.get(vid);
    if (!voie) continue;
    for (let r = 0; r < rang; r++) {
      const rankData = voie.rangs[r];
      if (!rankData?.effets) continue;
      for (const e of rankData.effets) {
        if (e.type !== 'bonus_stat') continue;
        const val = e.value?.kind === 'const' ? e.value.value : e.value?.kind === 'rang' ? rang : 0;
        switch (e.stat) {
          case 'DEF':
            bonuses.def += val;
            break;
          case 'INIT':
            bonuses.init += val;
            break;
          case 'PV':
            bonuses.pv += val;
            break;
          case 'PM':
            bonuses.pm += val;
            break;
        }
      }
      for (const e of rankData.effets) {
        if (e.type !== 'bonus_attaque') continue;
        const val = e.value?.kind === 'const' ? e.value.value : e.value?.kind === 'rang' ? rang : 0;
        if (e.mode === 'contact' || e.mode === 'all') bonuses.attContact += val;
        if (e.mode === 'distance' || e.mode === 'all') bonuses.attDistance += val;
        if (e.mode === 'magique' || e.mode === 'all') bonuses.attMagique += val;
      }
    }
  }

  // Maitre d'armes : +1 ATT avec arme de predilection
  if (p.choixVoies?.['voie-du-maitre-d-armes'] && (p.rangs['voie-du-maitre-d-armes'] ?? 0) >= 1) {
    const choix = p.choixVoies['voie-du-maitre-d-armes'];
    if (choix === 'Armes de jet') {
      bonuses.attDistance += 1;
    } else {
      bonuses.attContact += 1;
    }
  }

  // Archer/Lancer : +rang en Init (arc seulement)
  if (p.choixVoies?.['voie-de-l-archer'] && (p.rangs['voie-de-l-archer'] ?? 0) >= 1) {
    const choix = p.choixVoies['voie-de-l-archer'];
    if (choix === 'Arc') {
      bonuses.init += p.rangs['voie-de-l-archer'] ?? 0;
    }
  }

  // Pretre guerrier : +1 DEF si divinite guerriere + spiritualite rang 1
  if (
    p.diviniteId &&
    WARRIOR_DEITIES.includes(p.diviniteId) &&
    (p.rangs['voie-de-la-spiritualite'] ?? 0) >= 1
  ) {
    bonuses.def += 1;
  }

  return bonuses;
}

const EQUIP_SLOTS = [
  { key: 'tete', label: 'Tete' },
  { key: 'cou', label: 'Cou' },
  { key: 'epaules', label: 'Epaules' },
  { key: 'torse', label: 'Torse' },
  { key: 'dos', label: 'Dos' },
  { key: 'ceinture', label: 'Ceinture' },
  { key: 'poignet_1', label: 'Poignet G' },
  { key: 'poignet_2', label: 'Poignet D' },
  { key: 'jambieres', label: 'Jambieres' },
  { key: 'bottes', label: 'Bottes' },
  { key: 'main_1', label: 'Main droite' },
  { key: 'main_2', label: 'Main gauche' },
  { key: 'distance', label: 'Distance' },
  { key: 'bouclier', label: 'Bouclier' },
  { key: 'grimoire', label: 'Grimoire' },
  { key: 'doigt_1', label: 'Anneau 1' },
  { key: 'doigt_2', label: 'Anneau 2' },
] as const;

export async function renderPersonnageFichePage(id: string): Promise<unknown> {
  const p = await getPersonnage(id);
  if (!p) {
    return html`
      <h1 class="page-title">Personnage introuvable</h1>
      <p class="muted">Ce personnage n'existe pas ou a ete supprime.</p>
      <a class="btn btn-secondary" href=${buildHref('personnage')}>Retour a la liste</a>
    `;
  }

  const idx = await loadIndexes();
  const compagnonsData = await loadCompagnons();
  const profil = idx.bdd.profils.profils.find((pr) => pr.id === p.profilId);
  const peuple = idx.bdd.peuples.peuples.find((pe) => pe.id === p.peupleId);
  const familleId = (profil?.famille_id ?? 'aventuriers') as FamilleId;
  const fam = FAMILLES[familleId];

  const armureEquipee = p.equipement.armure
    ? (idx.bdd.equipement.armures ?? []).find((a) => a.nom === p.equipement.armure)
    : null;
  const bouclierEquipe = p.equipement.bouclier
    ? (idx.bdd.equipement.boucliers ?? []).find((b) => b.nom === p.equipement.bouclier)
    : null;

  const capBonuses = resolveCapBonuses(p, idx);

  let nbSortsConnus = 0;
  for (const vid of [...p.voiesProfil, ...(p.voiePeupleId ? [p.voiePeupleId] : [])]) {
    const rang = p.rangs[vid] ?? 0;
    const voie = idx.voieClasseById.get(vid) ?? idx.voiePeupleById.get(vid);
    if (!voie) continue;
    for (let r = 0; r < rang; r++) {
      if (voie.rangs[r]?.is_sort) nbSortsConnus++;
    }
  }

  const derivedInput: DerivedInput = {
    niveau: p.niveau,
    famille: familleId,
    caracs: p.caracs,
    nbSortsConnus,
    armureBonus: armureEquipee?.bonus_def ?? 0,
    bouclierBonus: bouclierEquipe?.bonus_def ?? 0,
    agiMax: armureEquipee?.agi_max ?? null,
    grandBouclier: bouclierEquipe?.bonus_def === 2,
    capBonuses,
  };
  let derived = calcDerived(derivedInput);

  if (p.profilSecondaireId) {
    const profilSec = idx.bdd.profils.profils.find((pr) => pr.id === p.profilSecondaireId);
    const famSec = (profilSec?.famille_id ?? familleId) as FamilleId;
    if (famSec !== familleId) {
      const pvFam1 = FAMILLES[familleId].pvPerLevel;
      const pvFam2 = FAMILLES[famSec].pvPerLevel;
      const hybridePV = pvFam1 + pvFam2 + p.caracs.CON * p.niveau + (capBonuses.pv ?? 0);
      derived = { ...derived, pv: Math.max(1, hybridePV) };
    }
  }

  const allVoieIds = [
    ...p.voiesProfil,
    ...(p.useVoieMage && p.rangs['voie-du-mage'] ? ['voie-du-mage'] : []),
  ];
  const VOIE_RENAMES: Record<string, Record<string, string>> = {
    'voie-de-l-archer': { 'Armes de jet': 'Voie du Lancer' },
  };

  const voies = allVoieIds
    .map((vid) => {
      const v = idx.voieClasseById.get(vid);
      const rang = p.rangs[vid] ?? 0;
      if (!v) return null;
      const choix = p.choixVoies?.[vid] ?? null;
      const rename = choix && VOIE_RENAMES[vid]?.[choix];
      const nom = rename ?? v.nom;
      const capacites = v.rangs.slice(0, rang).map((r) => ({
        nom: r.nom,
        type: r.type ?? 'passif',
        description: r.description ?? '',
      }));
      return { id: vid, nom, rang, capacites, choix };
    })
    .filter(Boolean);

  const voiePeuple = p.voiePeupleId ? idx.voiePeupleById.get(p.voiePeupleId) : null;
  const voiePeupleRang = p.voiePeupleId ? (p.rangs[p.voiePeupleId] ?? 1) : 0;
  const voiePeupleCapacites = voiePeuple
    ? voiePeuple.rangs.slice(0, voiePeupleRang).map((r) => ({
        nom: r.nom,
        type: r.type ?? 'passif',
        description: r.description ?? '',
      }))
    : [];

  const peupleBonus = peuple?.modificateurs_carac.bonus ?? [];
  const peupleMalus = peuple?.modificateurs_carac.malus ?? [];
  const caracDeltas: Record<string, number> = {};
  for (let i = 0; i < peupleBonus.length; i++) {
    const b = peupleBonus[i];
    if (!b) continue;
    if (b.carac === 'libre') {
      const choice = p.peupleChoices?.optionsChoices?.[i];
      if (typeof choice === 'string')
        caracDeltas[choice] = (caracDeltas[choice] ?? 0) + Number(b.valeur);
      continue;
    }
    const choice = p.peupleChoices?.optionsChoices?.[i];
    const key = b.alternatif ? (typeof choice === 'string' ? choice : b.carac) : b.carac;
    caracDeltas[key] = (caracDeltas[key] ?? 0) + Number(b.valeur);
  }
  for (let i = 0; i < peupleMalus.length; i++) {
    const m = peupleMalus[i];
    if (!m) continue;
    const choice = p.peupleChoices?.malusChoices?.[i];
    const key = m.alternatif ? (choice ?? m.carac) : m.carac;
    caracDeltas[key] = (caracDeltas[key] ?? 0) + Number(m.valeur);
  }

  const divinites =
    (
      idx.bdd.pantheon as {
        divinites?: { id: string; nom: string; arme_sacree: string | string[] }[];
      }
    )?.divinites ?? [];
  const divinite = p.diviniteId ? divinites.find((d) => d.id === p.diviniteId) : null;
  const diviniteNom = divinite?.nom ?? null;

  const hasStates = p.buffs.length > 0 || p.etats.length > 0;
  const equipSlots = p.equipement as Record<string, string | null>;
  equipErrors.length = 0;

  const allEquipItems = p.inventaire.filter((i) => i.cat === 'equipement');
  const equipStatsMap = new Map<string, string>();
  for (const item of allEquipItems) {
    const stats = findEquipStats(item.nom, idx);
    if (stats) equipStatsMap.set(item.nom, stats);
  }
  for (const val of Object.values(equipSlots)) {
    if (val && !equipStatsMap.has(val)) {
      const stats = findEquipStats(val, idx);
      if (stats) equipStatsMap.set(val, stats);
    }
  }
  const debugErrors = [...new Set(equipErrors)];

  return html`
    <div class="fiche">
      ${
        debugErrors.length > 0
          ? html`
        <div class="debug-banner">
          <strong>DEBUG</strong> Equipement sans correspondance BDD :
          ${debugErrors.map((e) => html`<code>${e}</code> `)}
        </div>
      `
          : nothing
      }
      <!-- HEADER -->
      <div class="fiche-hero">
        <div class="fiche-hero-left">
          <h1 class="fiche-name" data-morph data-morph-duration="1200">${p.nom}</h1>
          <span class="fiche-class">${profil?.nom ?? p.profilId}${diviniteNom ? ` de ${diviniteNom}` : ''}</span>
          <span class="fiche-people">${peuple?.nom ?? p.peupleId}</span>
        </div>
        <div class="fiche-hero-right">
          <div class="fiche-level">Niv. ${p.niveau}</div>
        </div>
      </div>

      ${
        p.concept
          ? html`
        <details class="fiche-collapse">
          <summary class="fiche-collapse-title">Concept</summary>
          <p class="fiche-concept">${p.concept}</p>
        </details>
      `
          : nothing
      }

      <!-- STATS PRINCIPALES (toujours visible) -->
      <section class="fiche-section">
        <div class="stats-bar">
          <div class="stat-big stat-pv ${pvColorClass(p.pvActuel ?? derived.pv, derived.pv)} tooltip-wrap">
            <span class="stat-big-value">${p.pvActuel ?? derived.pv}</span>
            <span class="stat-big-max">/ ${derived.pv}</span>
            <span class="stat-big-label">PV</span>
            <span class="tooltip-text">PV/niv x (Niv+1) + CON x Niv = ${fam.pvPerLevel} x ${p.niveau + 1} + ${p.caracs.CON} x ${p.niveau}</span>
          </div>
          <div class="stat-big tooltip-wrap">
            <span class="stat-big-value">${derived.def}</span>
            <span class="stat-big-label">DEF</span>
            <span class="tooltip-text">10 + AGI(${formatSign(p.caracs.AGI)})${derived.defDetail.armure ? ` + armure(+${derived.defDetail.armure})` : ''}${derived.defDetail.bouclier ? ` + bouclier(+${derived.defDetail.bouclier})` : ''}${derived.defDetail.capBonus ? ` + bonus(+${derived.defDetail.capBonus})` : ''}</span>
          </div>
          <div class="stat-big tooltip-wrap">
            <span class="stat-big-value">${derived.initiative}</span>
            <span class="stat-big-label">Init</span>
            <span class="tooltip-text">10 + PER = 10 + ${formatSign(p.caracs.PER)}</span>
          </div>
        </div>
        <div class="stats-secondary">
          <div class="stat-sm tooltip-wrap"><span class="stat-sm-label">PC</span><span class="stat-sm-value">${p.pcActuel ?? derived.pc}</span><span class="tooltip-text">2 + CHA${fam.bonusPC ? ` + ${fam.bonusPC} (${familleId})` : ''} = 2 + ${p.caracs.CHA}${fam.bonusPC ? ` + ${fam.bonusPC}` : ''}</span></div>
          ${derived.pm !== null ? html`<div class="stat-sm tooltip-wrap"><span class="stat-sm-label">PM</span><span class="stat-sm-value">${p.pmActuel ?? derived.pm}</span><span class="tooltip-text">VOL + nb sorts connus = ${p.caracs.VOL} + sorts</span></div>` : nothing}
          <div class="stat-sm tooltip-wrap"><span class="stat-sm-label">DR</span><span class="stat-sm-value">${p.drActuel ?? derived.drCount} x ${derived.drType}</span><span class="tooltip-text">2 + CON${fam.bonusDR ? ` + ${fam.bonusDR} (${familleId})` : ''} = 2 + ${p.caracs.CON}${fam.bonusDR ? ` + ${fam.bonusDR}` : ''}, de ${derived.drType}</span></div>
          <div class="stat-sm tooltip-wrap">
            <span class="stat-sm-label">Att.C</span><span class="stat-sm-value">${formatSign(derived.attContact)}</span>
            <span class="tooltip-text">Niv(max10) + FOR = ${Math.min(p.niveau, 10)} + ${formatSign(p.caracs.FOR)}</span>
          </div>
          <div class="stat-sm tooltip-wrap">
            <span class="stat-sm-label">Att.D</span><span class="stat-sm-value">${formatSign(derived.attDistance)}</span>
            <span class="tooltip-text">Niv(max10) + AGI = ${Math.min(p.niveau, 10)} + ${formatSign(p.caracs.AGI)}</span>
          </div>
          <div class="stat-sm tooltip-wrap">
            <span class="stat-sm-label">Att.M</span><span class="stat-sm-value">${formatSign(derived.attMagique)}</span>
            <span class="tooltip-text">Niv(max10) + VOL = ${Math.min(p.niveau, 10)} + ${formatSign(p.caracs.VOL)}</span>
          </div>
        </div>
      </section>

      <!-- CARACTERISTIQUES -->
      <details class="fiche-collapse" open>
        <summary class="fiche-collapse-title">Caracteristiques</summary>
        <div class="carac-grid">
          ${CARACS.map((k) => {
            const delta = caracDeltas[k] ?? 0;
            const colorClass = delta > 0 ? 'carac-bonus' : delta < 0 ? 'carac-malus' : '';
            const tooltipParts = [CARAC_ROLES[k]];
            if (delta !== 0) tooltipParts.push(`Peuple: ${delta > 0 ? '+' : ''}${delta}`);
            return html`
              <div class="carac-block tooltip-wrap ${colorClass}">
                <span class="carac-label">${k}</span>
                <span class="carac-value">${formatSign(p.caracs[k])}</span>
                <span class="tooltip-text">${tooltipParts.join(' | ')}</span>
              </div>
            `;
          })}
        </div>
      </details>

      <!-- ETATS / BUFFS -->
      ${
        hasStates
          ? html`
        <details class="fiche-collapse" open>
          <summary class="fiche-collapse-title">Etats actifs</summary>
          <div class="etats-list">
            ${p.etats.map((e) => html`<span class="etat-tag etat-negatif">${e.id}${e.tours !== null ? ` (${e.tours}t)` : ''}</span>`)}
            ${p.buffs.map((b) => html`<span class="etat-tag etat-positif">${b.id}${b.tours !== null ? ` (${b.tours}t)` : ''}</span>`)}
          </div>
        </details>
      `
          : nothing
      }

      <!-- EQUIPEMENT SLOTS -->
      <details class="fiche-collapse" open>
        <summary class="fiche-collapse-title">Equipement porte</summary>
        <div class="equip-slots">
          ${EQUIP_SLOTS.map(({ key, label }) => {
            const armureSlot = equipSlots.armure ?? null;
            const itemName = equipSlots[key] ?? (key === 'torse' ? armureSlot : null);
            const stats = itemName ? (equipStatsMap.get(itemName) ?? null) : null;
            const displayName = itemName;
            return html`
              <div class="equip-slot ${itemName ? '' : 'empty'} ${stats ? 'tooltip-wrap' : ''}">
                <span class="equip-slot-label">${label}</span>
                <span class="equip-slot-value">${displayName ?? '-'}</span>
                ${stats ? html`<span class="tooltip-text">${stats}</span>` : nothing}
              </div>
            `;
          })}
        </div>
      </details>

      <!-- BOURSE -->
      <details class="fiche-collapse" open>
        <summary class="fiche-collapse-title">Bourse</summary>
        <div class="bourse-grid">
          <div class="bourse-coin"><span class="coin-value">${p.bourse.po}</span><span class="coin-label">po</span></div>
          <div class="bourse-coin"><span class="coin-value">${p.bourse.pa}</span><span class="coin-label">pa</span></div>
          <div class="bourse-coin"><span class="coin-value">${p.bourse.pc}</span><span class="coin-label">pc</span></div>
        </div>
      </details>

      <!-- INVENTAIRE EQUIPEMENT -->
      ${
        p.inventaire.filter((i) => i.cat === 'equipement').length > 0
          ? html`
        <details class="fiche-collapse" open>
          <summary class="fiche-collapse-title">Armes et Armures</summary>
          <div class="inventaire-list">
            ${p.inventaire
              .filter((item) => item.cat === 'equipement')
              .map((item) => {
                const equipped = Object.values(equipSlots).some((v) => v === item.nom);
                const stats = equipStatsMap.get(item.nom) ?? null;
                const displayName = item.nom;
                return html`
                <div class="inventaire-item ${equipped ? 'equipped' : ''} ${stats ? 'tooltip-wrap' : ''}">
                  ${equipped ? html`<span class="equip-icon">E</span>` : nothing}
                  <span class="inventaire-nom">${displayName}</span>
                  ${item.qty > 1 ? html`<span class="inventaire-qty">x${item.qty}</span>` : nothing}
                  ${stats ? html`<span class="tooltip-text">${stats}</span>` : nothing}
                </div>
              `;
              })}
          </div>
        </details>
      `
          : nothing
      }

      <!-- INVENTAIRE MATERIEL -->
      ${
        p.inventaire.filter((i) => i.cat !== 'equipement').length > 0
          ? html`
        <details class="fiche-collapse">
          <summary class="fiche-collapse-title">Materiel</summary>
          <div class="inventaire-list">
            ${p.inventaire
              .filter((item) => item.cat !== 'equipement')
              .map(
                (item) => html`
              <div class="inventaire-item">
                <span class="inventaire-nom">${item.nom}</span>
                ${item.qty > 1 ? html`<span class="inventaire-qty">x${item.qty}</span>` : nothing}
              </div>
            `,
              )}
          </div>
        </details>
      `
          : nothing
      }

      <!-- VOIES & CAPACITES -->
      ${
        voies.length > 0 || voiePeuple
          ? html`
        <details class="fiche-collapse" open>
          <summary class="fiche-collapse-title">Voies et Capacites</summary>
          ${voiePeuple ? renderVoieBlock(voiePeuple.nom, voiePeupleRang, 5, voiePeupleCapacites) : nothing}
          ${voies.map((v) => (v ? renderVoieBlock(v.nom, v.rang, 5, v.capacites, v.choix) : nothing))}
        </details>
      `
          : nothing
      }

      <!-- COMPETENCES ET TALENTS -->
      ${(() => {
        const compBonuses = resolveCompetencesBonuses(p, idx);
        if (compBonuses.size === 0) return nothing;
        const sorted = [...compBonuses.entries()].sort((a, b) => b[1] - a[1]);
        return html`
          <details class="fiche-collapse" open>
            <summary class="fiche-collapse-title">Competences et Talents (${compBonuses.size})</summary>
            <div class="competences-grid">
              ${sorted.map(
                ([comp, val]) => html`
                <div class="competence-item">
                  <span class="competence-nom">${comp}</span>
                  <span class="competence-val">+${val}</span>
                </div>
              `,
              )}
            </div>
          </details>
        `;
      })()}

      <!-- COMPAGNONS -->
      ${(() => {
        const existingIds = new Set(p.compagnons.map((c) => c.compagnonBaseId));
        const detected: typeof p.compagnons = [];
        for (const vid of [...p.voiesProfil, ...(p.voiePeupleId ? [p.voiePeupleId] : [])]) {
          const rang = p.rangs[vid] ?? 0;
          const voie = idx.voieClasseById.get(vid) ?? idx.voiePeupleById.get(vid);
          if (!voie) continue;
          for (let r = 0; r < rang; r++) {
            const rankData = voie.rangs[r];
            if (!rankData?.effets) continue;
            for (const e of rankData.effets) {
              if (
                e.type === 'invocation' &&
                e.creature_id &&
                e.controle === 'total' &&
                !existingIds.has(e.creature_id)
              ) {
                existingIds.add(e.creature_id);
                detected.push({
                  id: crypto.randomUUID(),
                  nom: e.creature_id
                    .replace(/-/g, ' ')
                    .replace(/^\w/, (c: string) => c.toUpperCase()),
                  compagnonBaseId: e.creature_id,
                  voieSourceId: vid,
                  pvActuel: null,
                  etats: [],
                  buffs: [],
                  notes: '',
                });
              }
            }
          }
        }
        const allCompagnons = [...p.compagnons, ...detected];
        if (allCompagnons.length === 0) return nothing;
        return html`
        <details class="fiche-collapse" open>
          <summary class="fiche-collapse-title">Compagnons (${allCompagnons.length})</summary>
          <div class="compagnons-list">
            ${allCompagnons.map((comp) => {
              const base = compagnonsData.compagnons.find((c) => c.id === comp.compagnonBaseId);
              if (!base)
                return html`<div class="muted">Compagnon inconnu : ${comp.compagnonBaseId}</div>`;
              return renderCompagnon(
                comp,
                base,
                p.niveau,
                p.rangs[comp.voieSourceId] ?? 1,
                derived,
              );
            })}
          </div>
        </details>
        `;
      })()}

      <!-- LANGUES -->
      ${
        p.langues.length > 0
          ? html`
        <details class="fiche-collapse">
          <summary class="fiche-collapse-title">Langues</summary>
          <div class="trait-tags">
            ${p.langues.map((l) => html`<span class="trait-tag">${l}</span>`)}
          </div>
        </details>
      `
          : nothing
      }

      <!-- PERSONNALITE -->
      <details class="fiche-collapse">
        <summary class="fiche-collapse-title">Personnalite</summary>
        <div class="perso-traits">
          ${p.ideal ? html`<div class="perso-trait"><span class="perso-trait-label">Ideal</span><span>${p.ideal}</span></div>` : nothing}
          ${p.defaut ? html`<div class="perso-trait"><span class="perso-trait-label">Defaut</span><span>${p.defaut}</span></div>` : nothing}
          ${p.phobie ? html`<div class="perso-trait"><span class="perso-trait-label">Phobie</span><span>${p.phobie}</span></div>` : nothing}
          ${p.secretIntime ? html`<div class="perso-trait"><span class="perso-trait-label">Secret</span><span>${p.secretIntime}</span></div>` : nothing}
          ${p.talentSecondaire ? html`<div class="perso-trait"><span class="perso-trait-label">Talent</span><span>${p.talentSecondaire} (+3 aux tests)</span></div>` : nothing}
        </div>
      </details>

      <!-- ACTIONS -->
      <div class="fiche-actions">
        <a class="btn btn-secondary" href=${buildHref('personnage')}>Retour a la liste</a>
        <button class="btn btn-secondary" @click=${() => exportPersonnageJSON(p)}>Exporter JSON</button>
        <button class="btn btn-secondary" @click=${async () => {
          const copy = await duplicatePersonnage(p.id);
          if (copy) navigate('personnage-fiche', { id: copy.id });
        }}>Dupliquer</button>
      </div>
    </div>
  `;
}

function renderVoieBlock(
  nom: string,
  rang: number,
  max: number,
  capacites: { nom: string; type: string; description: string }[],
  choix?: string | null,
) {
  return html`
    <div class="fiche-voie-block">
      <div class="fiche-voie-header">
        <strong>${nom}</strong>
        ${choix ? html`<span class="compagnon-type-badge">${choix}</span>` : nothing}
        <span class="rang-dots">${renderRangDots(rang, max)}</span>
      </div>
      <div class="fiche-capacites">
        ${capacites.map(
          (c) => html`
          <div class="fiche-capacite">
            <div class="fiche-capacite-header">
              <span class="capacite-nom">${c.nom}</span>
              <span class="capacite-type">${c.type}</span>
            </div>
            <p class="capacite-desc">${c.description}</p>
          </div>
        `,
        )}
      </div>
    </div>
  `;
}

function resolveCompetencesBonuses(
  p: {
    voiesProfil: string[];
    voiePeupleId: string | null;
    rangs: Record<string, number>;
    talentSecondaire: string | null;
  },
  idx: BDDIndexes,
): Map<string, number> {
  const bonuses = new Map<string, number>();
  const allVoieIds = [...p.voiesProfil, ...(p.voiePeupleId ? [p.voiePeupleId] : [])];

  for (const vid of allVoieIds) {
    const rang = p.rangs[vid] ?? 0;
    const voie = idx.voieClasseById.get(vid) ?? idx.voiePeupleById.get(vid);
    if (!voie) continue;
    for (let r = 0; r < rang; r++) {
      const rankData = voie.rangs[r];
      if (!rankData?.effets) continue;
      for (const e of rankData.effets) {
        if (e.type !== 'bonus_test') continue;
        const val =
          e.value?.kind === 'const'
            ? e.value.value
            : e.value?.kind === 'rang_plus'
              ? rang + (e.value.delta ?? 0)
              : e.value?.kind === 'rang'
                ? rang
                : 0;
        for (const comp of e.competences ?? []) {
          const key = comp.toLowerCase();
          bonuses.set(key, (bonuses.get(key) ?? 0) + val);
        }
      }
    }
  }

  if (p.talentSecondaire) {
    for (const t of p.talentSecondaire.split(', ')) {
      if (t) {
        const key = t.toLowerCase();
        bonuses.set(key, (bonuses.get(key) ?? 0) + 3);
      }
    }
  }

  return bonuses;
}

function renderCompagnon(
  comp: {
    id: string;
    nom: string;
    compagnonBaseId: string;
    voieSourceId: string;
    pvActuel: number | null;
  },
  base: CompagnonBase,
  niveauProprio: number,
  rangVoie: number,
  derivedProprio: { initiative: number; attMagique: number },
) {
  const pvMax = evalFormula(base.stats_base.pv, niveauProprio, rangVoie);
  const def = evalFormula(base.stats_base.def, niveauProprio, rangVoie);
  const att =
    base.stats_base.att === 'att_magique_proprietaire'
      ? derivedProprio.attMagique
      : evalFormula(base.stats_base.att, niveauProprio, rangVoie);
  const dm = base.stats_base.dm;
  const init = base.initiative_regles === 'egale_a_proprietaire' ? derivedProprio.initiative : 10;
  const mods = base.stats_base.modificateurs ?? {};

  return html`
    <div class="compagnon-card">
      <div class="compagnon-header">
        <strong class="compagnon-nom">${comp.nom}</strong>
        <span class="compagnon-type-badge">${base.type.replace(/_/g, ' ')}</span>
        <span class="muted">${base.voie_source_id.replace(/-/g, ' ')} (rang ${rangVoie})</span>
      </div>
      <div class="compagnon-stats">
        <div class="comp-stat comp-pv ${pvColorClass(comp.pvActuel ?? pvMax, pvMax)} tooltip-wrap">
          <span class="comp-stat-value">${comp.pvActuel ?? pvMax}</span>
          <span class="comp-stat-max">/ ${pvMax}</span>
          <span class="comp-stat-label">PV</span>
          <span class="tooltip-text">${base.stats_base.pv} = ${pvMax}</span>
        </div>
        <div class="comp-stat tooltip-wrap">
          <span class="comp-stat-value">${def}</span>
          <span class="comp-stat-label">DEF</span>
          <span class="tooltip-text">${base.stats_base.def} = ${def}</span>
        </div>
        <div class="comp-stat tooltip-wrap">
          <span class="comp-stat-value">${formatSign(att)}</span>
          <span class="comp-stat-label">ATT</span>
          <span class="tooltip-text">${base.stats_base.att === 'att_magique_proprietaire' ? 'Att. magique du proprietaire' : base.stats_base.att}</span>
        </div>
        <div class="comp-stat tooltip-wrap">
          <span class="comp-stat-value">${dm}</span>
          <span class="comp-stat-label">DM</span>
          <span class="tooltip-text">Degats de base</span>
        </div>
        <div class="comp-stat tooltip-wrap">
          <span class="comp-stat-value">${init}</span>
          <span class="comp-stat-label">INIT</span>
          <span class="tooltip-text">${base.initiative_regles === 'egale_a_proprietaire' ? '= Initiative du proprietaire' : 'Initiative propre'}</span>
        </div>
      </div>
      <div class="compagnon-caracs">
        ${Object.entries(mods).map(([k, v]) => {
          const num = Number(v);
          const cls = num > 0 ? 'carac-bonus' : num < 0 ? 'carac-malus' : '';
          const role = CARAC_ROLES[k.toUpperCase() as keyof typeof CARAC_ROLES] ?? '';
          return html`<span class="comp-carac ${cls} tooltip-wrap">${k.toUpperCase()} ${num >= 0 ? '+' : ''}${num}<span class="tooltip-text">${role}</span></span>`;
        })}
      </div>
    </div>
  `;
}

function evalFormula(formula: string, niveau: number, rang: number): number {
  if (!formula) return 0;
  const s = formula
    .replace(/niveau_proprietaire/g, String(niveau))
    .replace(/rang_voie/g, String(rang));
  try {
    return Math.floor(Function(`"use strict"; return (${s})`)());
  } catch {
    return 0;
  }
}
